<template>
  <v-navigation-drawer
    fixed
    :class="[{ mobile: $vuetifyDisplay.smAndDown }]"
    hide-overlay
    id="instructions-drawer"
    right
    style="z-index: 300"
    touchless
    temporary
    :permanent="show"
    :value="show"
    :width="drawerWidth"
    @input="handleNavigationDrawerInput"
  >
    <v-row dense class="pt-0">
      <v-col cols="12" class="pa-0">
        <v-card flat class="py-0">
          <v-card-title
            class="i-dialog-header-div py-2 px-4 blue-grey lighten-4"
          >
            <v-row dense>
              <v-col cols="12" class="pa-0">
                <v-btn
                  color="primary"
                  outlined
                  elevation="0"
                  rounded
                  text
                  x-small
                  @click="handleClose"
                  ><v-icon small>mdi-chevron-double-right</v-icon>
                  Collapse</v-btn
                >
              </v-col>
              <v-col cols="12" class="pa-0 pb-1 text-center">
                <span class="g-heading-m g-bold">Instructions</span>
              </v-col>
            </v-row>
          </v-card-title>

          <v-card-text v-if="fetching" class="text-center">
            <v-progress-circular
              color="primary"
              size="40"
              indeterminate
              class="my-10"
            />
          </v-card-text>
          <v-card-text v-else class="px-3">
            <template v-if="instruction">
              <v-row dense class="mb-1">
                <v-col
                  class="px-2"
                  :cols="tabColumns"
                  v-for="section in instruction.sections"
                  :key="section.id"
                >
                  <v-btn
                    block
                    class="i-btn-instruction-tab py-6"
                    :class="{
                      'i-btn-instruction-tab-active':
                        selectedInstructionSection === section.title
                    }"
                    elevation="0"
                    @click="handleTabClick(section.title)"
                  >
                    {{ section.title }}
                  </v-btn>
                </v-col>
              </v-row>
              <template v-for="section in instruction.sections">
                <div
                  v-if="selectedInstructionSection === section.title"
                  class="pb-10"
                  id="instruction-text"
                  v-html="parseSectionContent(section.content)"
                  :key="section.id"
                ></div>
              </template>
            </template>
            <div v-else class="text-center pt-6">
              No instructions available yet!
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-navigation-drawer>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  components: {},

  props: {},

  data: () => ({
    selectedInstructionSection: undefined
  }),

  computed: {
    ...mapState('instructions', [
      'show',
      'fetching',
      'instruction',
      'instructionSection'
    ]),

    legacyViewportWidth() {
      // $vuetifyDisplay.width tracks Vuetify's reactive width ref, so this
      // computed re-evaluates on resize - a direct window.innerWidth read
      // has no Vue reactivity tracking and would only update on the next
      // unrelated re-render.
      return this.$vuetifyDisplay.width
    },

    tabsPerRow() {
      return this.legacyViewportWidth >= 1904 ? 2 : 1 // Change this depending on how many tabs you want display in one row
    },

    tabColumns() {
      return 12 / this.tabsPerRow
    },

    drawerWidth() {
      const widthAsPercentOfViewport = (percentage) =>
        `${this.legacyViewportWidth * (percentage / 100)}px`

      if (this.$vuetifyDisplay.smAndDown) return widthAsPercentOfViewport(90)
      else if (this.$vuetifyDisplay.mdAndDown)
        return widthAsPercentOfViewport(40)
      else if (this.legacyViewportWidth < 1904)
        return widthAsPercentOfViewport(20)
      return widthAsPercentOfViewport(22)
    }
  },

  methods: {
    ...mapActions('instructions', ['hideInstructions']),

    handleTabClick(sectionTitle) {
      this.selectedInstructionSection = sectionTitle
    },

    handleNavigationDrawerInput(visible) {
      if (!visible) this.handleClose()
    },

    handleClose() {
      this.hideInstructions()
    },

    parseSectionContent(html) {
      return html.replace(
        'support@flewidtech.com',
        '<a href="mailto:support@flewidtech.com">support@flewidtech.com</a>'
      )
    }
  },

  mounted() {
    this.selectedInstructionSection =
      this.instructionSection || this.instruction?.sections[0]?.title
  },

  watch: {
    instruction() {
      this.selectedInstructionSection =
        this.instructionSection || this.instruction?.sections[0]?.title
      this.handleTabClick(this.selectedInstructionSection)
    },
    instructionSection() {
      this.selectedInstructionSection =
        this.instructionSection || this.instruction?.sections[0]?.title
      this.handleTabClick(this.selectedInstructionSection)
    }
  }
}
</script>
<style lang="scss" scoped>
#instructions-drawer {
  .i-btn-instruction-tab {
    background-color: lighten(gray, 40%);
    border-bottom: 5px solid gray;
    color: darken(gray, 30%);
    &:hover {
      background-color: lighten(gray, 40%);
    }

    &-active {
      &:hover {
        background-color: var(--v-secondary-lighten1);
      }
      color: white;
      background-color: $secondary-color;
      border-bottom: 5px solid $primary-color;
    }
  }
}
</style>
<style lang="scss">
#instructions-drawer {
  top: 64px !important;
  > *,
  .v-sheet {
    background-color: #f9f9ff !important;
  }

  &.mobile {
    top: 56px !important;
  }

  .v-btn * {
    font-size: 0.95rem !important;
  }
}

#instruction-text {
  padding: 10px 0 10px 10px;
  margin-bottom: 80px;
  * {
    font-size: 0.9rem !important;
  }

  ol,
  ul {
    margin-top: 10px;
    margin-bottom: 10px;
  }

  li {
    margin-bottom: 4px;
    margin-top: 4px;
  }

  a {
    text-decoration: underline;
  }

  h1 {
    font-size: 1.5em !important;
    line-height: 1.5em !important;
    padding-bottom: 8px;
  }

  h2 {
    font-size: 1.25em !important;
    line-height: 1.25em !important;
    padding-bottom: 4px;
  }

  h3 {
    font-size: 1.1em !important;
    line-height: 1.1em !important;
    padding-bottom: 2px;
  }
}
</style>
